import type { Sprint } from '../../api/types';

/**
 * PH-37 — "how much of this sprint is done, and how much is left?", for the sprint header.
 *
 * Or: *"did planhub UI have sum of story point on-progress and done so we know what left in each
 * sprint"*. Before this the header read `7 work items · 32 pts` and stopped, so a RUNNING sprint
 * could not be read for progress at all.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════════
 * 🔴 THIS FILE DOES NOT DECIDE WHAT "DONE" MEANS, AND THAT IS THE WHOLE DESIGN.
 *
 * The API decides, in SQL, from `status_def.is_final` and the lane of the project's own
 * `initial_status` — see its `src/services/sprint-progress.ts`. Two reasons this client must not
 * re-derive it, and the second is the decisive one:
 *
 *   1. `is_final` is ADMIN-EDITABLE (Or, 2026-08-17) and statuses are addable from the settings page.
 *      A status list typed into a client goes stale the first time somebody marks a new status final,
 *      and it goes stale SILENTLY — the totals still render, just wrong. That is the failure shape
 *      this ticket exists to prevent, and `sprint-close.ts` in this same folder already argues it for
 *      carry-over: *"a hard-coded status list there would go stale the day a status is added, and it
 *      would go stale SILENTLY"*.
 *   2. ⭐⭐ THIS CLIENT COULD NOT REPRODUCE THE RULE EVEN IF IT WANTED TO. "Not started" is the lane
 *      of `project.initial_status`, and this client is never sent that field. So a client-side split
 *      would have to guess — i.e. hard-code the lane `'intake'` — which is a hard-coded vocabulary
 *      value one level up from a status code, with the identical failure mode.
 *   ⇒ The seven numbers arrive computed. This file's ONLY job is to decide whether they may be
 *     BELIEVED, and to say what they mean.
 *
 * ⚠️ WHICH IS WHY THE INTERESTING LOGIC HERE IS ALL REFUSAL. See `sprintProgress` below: it returns
 *   `null` — no split, old header, nothing lost — rather than draw a bar from numbers that are
 *   absent, non-finite, negative, or that do not add up to the totals printed beside them.
 */

/**
 * How to read these numbers against what is on screen.
 *
 *   exact        — the sprint holds exactly what the section is showing.
 *   whole-sprint — a filter is on, so the rows on screen are a SUBSET while these buckets are the
 *                  whole sprint. ⛔ Printed with the `*` marker the header already uses for this
 *                  exact situation on `ticket_count`/`story_points_total`, because a caveat that
 *                  exists only in a tooltip is a caveat nobody on a phone can reach.
 *
 * ⛔ There is deliberately no `filtered-exact` state. The API's buckets know nothing about the status
 *   or assignee filter — recomputing them for the filtered view is impossible here (see the header),
 *   and printing whole-sprint numbers as though they were filtered ones is the "confident wrong
 *   number" this codebase refuses everywhere else.
 */
export type ProgressScope = 'exact' | 'whole-sprint';

export interface ProgressBucket {
  count: number;
  points: number;
}

export interface SprintProgressSummary {
  /** In a FINAL status — exactly what completing the sprint will NOT carry over. */
  done: ProgressBucket;
  /** Not final, and out of the project's entry lane. */
  inProgress: ProgressBucket;
  /** Not final, still in the project's entry lane. */
  notStarted: ProgressBucket;
  /**
   * `inProgress + notStarted` — THE ANSWER TO OR'S QUESTION, computed here rather than left to each
   * caller so "what is left" has one definition in this app.
   */
  remaining: ProgressBucket;
  total: ProgressBucket;
  /**
   * Outstanding tickets with no estimate.
   *
   * 🔴 WHAT AN UNESTIMATED TICKET DOES TO "REMAINING", STATED ONCE: it adds 1 to `remaining.count`
   *   and 0 to `remaining.points`. ⛔ It is NOT counted as zero points — "no estimate" and "estimated
   *   at zero" are different facts and only one of them means there is no work left.
   * ⭐⭐ So `remaining.points` is a FLOOR whenever this is above zero, and the header says so. A
   *   sprint reading "0 pts left" with three unestimated tickets open is precisely the misleading
   *   number PH-37 was raised about, arriving from the other direction.
   */
  unestimatedRemaining: number;
  scope: ProgressScope;
  /**
   * 0–100, for the bar.
   *
   * ⚠️ BY POINTS WHEN THERE ARE POINTS, BY ITEM COUNT WHEN THERE ARE NONE — and `basis` says which,
   *   because they are different claims. A sprint where nothing is estimated has `total.points === 0`
   *   and a points-based bar would sit at 0% forever while the team finished every ticket; falling
   *   back to counts makes it move. ⛔ It is not silently mixed: a bar that means two things
   *   depending on data nobody can see is worse than one that says which it means.
   */
  donePercent: number;
  basis: 'points' | 'items';
}

/** The seven fields, named once so the presence check and the docs cannot drift apart. */
const PROGRESS_FIELDS = [
  'story_points_done',
  'story_points_in_progress',
  'story_points_not_started',
  'ticket_count_done',
  'ticket_count_in_progress',
  'ticket_count_not_started',
  'unestimated_remaining_count',
] as const satisfies readonly (keyof Sprint)[];

/** ⚠️ NUMERIC sums accumulate float noise (0.1 + 0.2). Two decimals is `story_points`' own precision. */
const POINT_EPSILON = 0.005;

/**
 * The split for one sprint, or `null` when we are not entitled to draw one.
 *
 * 🔴 THE FOUR REFUSALS, each a real way for a wrong bar to reach a reader:
 *
 *   1. A FIELD IS ABSENT. A web build deployed against an API from before PH-37 gets `undefined`,
 *      and `undefined ?? 0` would render a confident "0% done" over a finished sprint. ⭐ This is the
 *      seam the review gate names as where every serious bug this project has shipped has lived —
 *      the web tests mock the API and the API tests do not know the web exists, so a shape mismatch
 *      passes both suites. ⇒ Absent means NO SPLIT, and the pre-PH-37 header stands unharmed.
 *   2. A VALUE IS NOT A FINITE NUMBER. `NUMERIC` reaches JSON as a string if a cast is ever dropped
 *      server-side, and `"13" + "5"` is `"135"` — the concatenation hazard `story_points_total`'s own
 *      comment was written about. A string that happens to be numeric is accepted (`Number()`), a
 *      string that is not is a refusal.
 *   3. A VALUE IS NEGATIVE. Not reachable through `count(*)`/`sum()`, and that is the point: if one
 *      ever arrives, something upstream is wrong in a way this file cannot interpret.
 *   4. ⭐⭐ THE BUCKETS DO NOT ADD UP. The API builds them to be exhaustive and disjoint, so they sum
 *      to the totals BY CONSTRUCTION — which makes a mismatch proof that the construction changed.
 *      The dangerous shape is a ticket in NO bucket: the buckets stay plausible, the bar looks fine,
 *      and work has quietly vanished from the count of what is left. ⛔ That is the exact bug PH-37
 *      was raised about, so it is refused rather than rendered. This check is cheap and it is the
 *      only one that can catch a server that regressed while still answering 200.
 */
export function sprintProgress(sprint: Sprint, filtered: boolean): SprintProgressSummary | null {
  const values: Record<string, number> = {};
  for (const field of PROGRESS_FIELDS) {
    const raw = sprint[field];
    if (raw === undefined || raw === null) return null; // (1)
    const numeric = Number(raw);
    if (!Number.isFinite(numeric) || numeric < 0) return null; // (2) and (3)
    values[field] = numeric;
  }

  const totalPoints = Number(sprint.story_points_total);
  const totalCount = Number(sprint.ticket_count);
  if (!Number.isFinite(totalPoints) || !Number.isFinite(totalCount)) return null;

  const done: ProgressBucket = {
    count: values.ticket_count_done as number,
    points: values.story_points_done as number,
  };
  const inProgress: ProgressBucket = {
    count: values.ticket_count_in_progress as number,
    points: values.story_points_in_progress as number,
  };
  const notStarted: ProgressBucket = {
    count: values.ticket_count_not_started as number,
    points: values.story_points_not_started as number,
  };

  // (4) — the sum invariant. Counts are integers and must match exactly; points tolerate float noise.
  if (done.count + inProgress.count + notStarted.count !== totalCount) return null;
  if (Math.abs(done.points + inProgress.points + notStarted.points - totalPoints) > POINT_EPSILON) {
    return null;
  }

  const remaining: ProgressBucket = {
    count: inProgress.count + notStarted.count,
    points: round2(inProgress.points + notStarted.points),
  };

  const basis: 'points' | 'items' = totalPoints > 0 ? 'points' : 'items';
  const numerator = basis === 'points' ? done.points : done.count;
  const denominator = basis === 'points' ? totalPoints : totalCount;
  const donePercent = denominator > 0 ? clampPercent((numerator / denominator) * 100) : 0;

  return {
    done,
    inProgress,
    notStarted,
    remaining,
    total: { count: totalCount, points: round2(totalPoints) },
    unestimatedRemaining: values.unestimated_remaining_count as number,
    scope: filtered ? 'whole-sprint' : 'exact',
    donePercent,
    basis,
  };
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

function clampPercent(value: number): number {
  if (value < 0) return 0;
  if (value > 100) return 100;
  return Math.round(value);
}

/**
 * The header sentence, SPLIT AT THE POINT WHERE IT IS ALLOWED TO BE CUT.
 *
 * 🔴 PH-51 — the whole sentence used to be one truncating box, and at 390px it rendered
 *   `6.5 pts left of 23.5 · 17 done · 3 op…`. The count survived; the noun did not. That is the
 *   worst possible half of this sentence to keep, because the ONLY reason the qualifier exists is
 *   that a bare number over unestimated tickets reads as finished — and `3 op…` is a digit with
 *   nothing attached to it, which reads as nothing at all.
 *
 * ⭐ SO THE TWO HALVES HAVE DIFFERENT TRUNCATION RIGHTS, and only the caller can enforce that — a
 *   single string cannot. `head` may be clipped: losing the tail of `… · 17 done` costs a
 *   secondary figure while the lead figure, which is the one Or asked for, is at the front and
 *   survives. `unestimated` may NOT be clipped, so it is handed over separately and kept short
 *   enough to always fit.
 *
 * ⚠️ `unestimated` IS DELIBERATELY TERSER THAN THE PROSE — `3 unsized`, not `3 open items have no
 *   estimate`. Width is the constraint that broke this, so the visible form is bought down to a
 *   number and a noun, which is the irreducible pair. The full wording is not lost: it is what
 *   `describeProgress` still returns for the accessible name, and `describeProgressDetail`
 *   explains the consequence ("the points left are a floor") in the tooltip.
 */
export interface ProgressSentence {
  /** The figures. Safe to clip — the lead number is first. */
  head: string;
  /** `null` when every open ticket is estimated. When present it MUST render in full. */
  unestimated: string | null;
}

export function describeProgressParts(progress: SprintProgressSummary): ProgressSentence {
  const unit = progress.basis === 'points' ? 'pts' : 'items';
  const left = progress.basis === 'points' ? progress.remaining.points : progress.remaining.count;
  const total = progress.basis === 'points' ? progress.total.points : progress.total.count;
  const done = progress.basis === 'points' ? progress.done.points : progress.done.count;

  return {
    head: `${formatNumber(left)} ${unit} left of ${formatNumber(total)} · ${formatNumber(done)} done`,
    unestimated: progress.unestimatedRemaining > 0 ? `${progress.unestimatedRemaining} unsized` : null,
  };
}

/**
 * The one-line summary as ONE string — the accessible name, and anywhere a sentence is wanted
 * rather than a layout.
 *
 * ⭐ "LEFT" IS THE LEAD, because that is the question Or asked — not "18 done", which is the same
 *   fact stated in the direction nobody was asking about. `14 pts left of 32 · 18 done`.
 *
 * ⚠️ THE UNESTIMATED QUALIFIER IS PART OF THE SENTENCE, not a footnote: with three unestimated
 *   tickets open, "0 pts left" is true and misleading, and "0 pts left of 32 · 3 items with no
 *   estimate" is true and not. ⛔ This spells it out in full where the visible chip says `3
 *   unsized`: a screen reader has no width limit, so it should not pay the price of one.
 */
export function describeProgress(progress: SprintProgressSummary): string {
  const { head } = describeProgressParts(progress);
  if (progress.unestimatedRemaining === 0) return head;

  const n = progress.unestimatedRemaining;
  return `${head} · ${n} open ${n === 1 ? 'item has' : 'items have'} no estimate`;
}

/**
 * The full breakdown, for the `title` attribute — every number, including the two the one-liner
 * folds together into "left".
 *
 * ⛔ NOT the only place the caveat appears. `scope` also drives a visible `*` on the header, for the
 *   reason `SprintSection` already states about `whole-sprint`: hover does not exist on the phone Or
 *   reviews this on.
 */
export function describeProgressDetail(progress: SprintProgressSummary): string {
  const bucket = (label: string, value: ProgressBucket) =>
    `${label}: ${value.count} item${value.count === 1 ? '' : 's'}, ${formatNumber(value.points)} pts`;

  const lines = [
    bucket('Done', progress.done),
    bucket('In progress', progress.inProgress),
    bucket('Not started', progress.notStarted),
  ];

  if (progress.unestimatedRemaining > 0) {
    lines.push(
      `${progress.unestimatedRemaining} unfinished item${
        progress.unestimatedRemaining === 1 ? '' : 's'
      } carry no estimate, so the points left are a floor.`,
    );
  }

  lines.push(
    'Done means the ticket is in a FINAL status — exactly what completing this sprint will leave behind. Not started means it has not left the status tickets are created in.',
  );

  if (progress.scope === 'whole-sprint') {
    lines.push(
      'These count the WHOLE sprint, not the filtered view — they come from the sprint itself, which knows nothing about the status or assignee filter.',
    );
  }

  return lines.join('\n');
}

/** `13` · `13.5`. The same rule as `formatPoints`, applied to an already-numeric total. */
function formatNumber(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}
